import type {
  ContentBlock,
  V2ConversationTurn,
  WidgetBlock,
} from '@shared/conversation-schema';

/**
 * Client-side id-keyed widget replace (spec 004-p6, sub-phase 6a — T015).
 *
 * ONE helper shared by both the text (SSE) and voice paths in `useConversation`.
 * Materialized roster-projection cards carry deterministic, engagement-scoped
 * `widget_id`s (`sc-card:{mode}:{engagementId}:{kind}`, contracts/card-events.md
 * §Identity). When a card with an id already present in `turns` arrives, we don't
 * stack a second copy — we update the single live card IN PLACE (see "Damping"
 * below), or — for a hypothetical non-materialized reused id — re-anchor it at
 * the conversation frontier with a `card_moved` breadcrumb chip left where it
 * used to be, so the thread reads as "this one card evolved" rather than "here
 * are eight versions".
 *
 * Replace semantics (contracts/card-events.md §Replace semantics — the "move" path):
 *   1. Remove the previous instance from its turn.
 *   2. Insert a `card_moved` chip marker at its former index.
 *   3. Append the new block at the frontier.
 *   4. (announce/reduced-motion handled by the chip component, T017.)
 *
 * Damping (plan R10, widened past a fixed window): a Cloud-materialized card
 * (`sc-card:` id) ALWAYS updates in place, with NO move and NO chip, regardless
 * of how far it has scrolled from the frontier — moving it re-mounted a FRESH
 * React element at the new position (a different `widget_id`-keyed element still
 * cannot preserve component identity across a different tree position), which
 * played `WidgetUpdateSurface`'s pop+glow MOUNT cue instead of its intended
 * in-place skeleton refresh. A non-materialized id (none exist in practice today)
 * still uses the original bounded window: within the last `dampingWindow`
 * (default 2) blocks → in place; further back → moved + chip. Either way, any
 * stale marker for the same id is dropped before a fresh update so at most one
 * chip per card ever exists at a time.
 *
 * Pure and framework-free so it can be unit-tested directly; `useConversation`
 * threads its minted turn id / timestamp in.
 */

/** `widget_type` of the breadcrumb marker; rendered by `CardMovedChip` (T017). */
export const CARD_MOVED_WIDGET_TYPE = 'card_moved';

/** `widget_id` prefix that makes a marker's own React key distinct from the card it points at. */
const CARD_MOVED_ID_PREFIX = 'card-moved:';

/** Prefix marking a deterministic, engagement-scoped materialized-card id (contracts §Identity). */
export const MATERIALIZED_CARD_ID_PREFIX = 'sc-card:';

const DEFAULT_DAMPING_WINDOW = 2;

/** A runtime-minted assistant turn coalesces new frontier content (mirrors `appendBlockToAssistantTurn`). */
const RUNTIME_ASSISTANT_TURN = /^assistant_\d+_\d+$/;

/** Props a `card_moved` marker block carries in `data`. */
export interface CardMovedData {
  /** The `widget_id` of the live card this breadcrumb points at (scroll target). */
  widget_id: string;
  /** The turn id the live card re-anchored into. */
  moved_to_turn: string;
}

/** True for a `card_moved` breadcrumb marker (a widget block with the marker type). */
export function isCardMovedBlock(block: ContentBlock): boolean {
  return block.type === 'widget' && block.widget_type === CARD_MOVED_WIDGET_TYPE;
}

/** Build a `card_moved` breadcrumb marker for a card that re-anchored elsewhere. */
export function makeCardMovedMarker(targetWidgetId: string, movedToTurn: string): WidgetBlock {
  return {
    type: 'widget',
    widget_type: CARD_MOVED_WIDGET_TYPE,
    // The marker's OWN key is distinct from the card's `w:${widget_id}` key so
    // the two never collide as React keys in the transcript.
    widget_id: `${CARD_MOVED_ID_PREFIX}${targetWidgetId}`,
    data: { widget_id: targetWidgetId, moved_to_turn: movedToTurn } satisfies CardMovedData,
  };
}

interface WidgetLocation {
  turnIndex: number;
  blockIndex: number;
}

/** Locate the live (non-marker) widget block matching `widgetId`, scanning last-first for the most recent. */
function findWidgetLocation(
  turns: V2ConversationTurn[],
  widgetId: string,
): WidgetLocation | null {
  for (let t = turns.length - 1; t >= 0; t--) {
    const content = turns[t].content;
    for (let b = content.length - 1; b >= 0; b--) {
      const block = content[b];
      if (block.type === 'widget' && !isCardMovedBlock(block) && block.widget_id === widgetId) {
        return { turnIndex: t, blockIndex: b };
      }
    }
  }
  return null;
}

/** Remove every `card_moved` marker pointing at `targetWidgetId` (drops turns left empty). */
function removeMarkersFor(
  turns: V2ConversationTurn[],
  targetWidgetId: string,
): V2ConversationTurn[] {
  const out: V2ConversationTurn[] = [];
  for (const turn of turns) {
    let changed = false;
    const content = turn.content.filter((block) => {
      const isMarkerForTarget =
        isCardMovedBlock(block) &&
        (block as WidgetBlock).data?.widget_id === targetWidgetId;
      if (isMarkerForTarget) changed = true;
      return !isMarkerForTarget;
    });
    if (content.length === 0 && changed) continue; // marker-only turn disappears
    out.push(changed ? { ...turn, content } : turn);
  }
  return out;
}

/** Flattened block index of a location + the total block count across all turns. */
function flatPosition(
  turns: V2ConversationTurn[],
  loc: WidgetLocation,
): { flatIndex: number; total: number } {
  let flatIndex = -1;
  let total = 0;
  for (let t = 0; t < turns.length; t++) {
    for (let b = 0; b < turns[t].content.length; b++) {
      if (t === loc.turnIndex && b === loc.blockIndex) flatIndex = total;
      total++;
    }
  }
  return { flatIndex, total };
}

/** Replace the block at `loc` with `replacement`, preserving position. */
function replaceBlockAt(
  turns: V2ConversationTurn[],
  loc: WidgetLocation,
  replacement: ContentBlock,
): V2ConversationTurn[] {
  return turns.map((turn, t) => {
    if (t !== loc.turnIndex) return turn;
    const content = turn.content.map((block, b) => (b === loc.blockIndex ? replacement : block));
    return { ...turn, content };
  });
}

/** Append `block` at the conversation frontier; returns the new turns + the turn id it landed in. */
function appendAtFrontier(
  turns: V2ConversationTurn[],
  block: ContentBlock,
  newTurnId: string,
  timestamp: string,
): { turns: V2ConversationTurn[]; turnId: string } {
  const last = turns[turns.length - 1];
  if (last?.role === 'assistant' && RUNTIME_ASSISTANT_TURN.test(last.turn_id)) {
    const updated: V2ConversationTurn = { ...last, content: [...last.content, block] };
    return { turns: [...turns.slice(0, -1), updated], turnId: last.turn_id };
  }
  return {
    turns: [
      ...turns,
      { turn_id: newTurnId, role: 'assistant', content: [block], timestamp },
    ],
    turnId: newTurnId,
  };
}

export interface ApplyWidgetReplaceParams {
  /** Current transcript turns. */
  turns: V2ConversationTurn[];
  /** The incoming widget block (its `widget_id` keys the replace). */
  block: WidgetBlock;
  /** A pre-minted assistant turn id, used only if a fresh frontier turn is created. */
  newTurnId: string;
  /** Timestamp for a fresh frontier turn (defaults to now). */
  timestamp?: string;
  /** How many trailing blocks count as "still in view" for damping (default 2). */
  dampingWindow?: number;
}

/**
 * Apply id-keyed replace for an incoming widget block. See the module doc for
 * the full contract. Returns new `turns`; never mutates the input.
 */
export function applyWidgetReplace(params: ApplyWidgetReplaceParams): V2ConversationTurn[] {
  const { turns, block, newTurnId } = params;
  const timestamp = params.timestamp ?? new Date().toISOString();
  const dampingWindow = params.dampingWindow ?? DEFAULT_DAMPING_WINDOW;
  const targetId = block.widget_id;

  // (1) Drop any stale breadcrumb for this id first — bounds chip count (R10).
  const cleaned = removeMarkersFor(turns, targetId);

  // (2) Find the live card instance for this id.
  const loc = findWidgetLocation(cleaned, targetId);

  // New card (or a model-minted unique id) → plain frontier append.
  if (!loc) {
    return appendAtFrontier(cleaned, block, newTurnId, timestamp).turns;
  }

  // (3) Damping: the live card is still within view → update in place, no chip.
  // Cloud-materialized capture cards (deterministic `sc-card:` ids) ALWAYS take
  // this branch, regardless of how far they've scrolled from the frontier: they
  // are a small, fixed set of persistent artifact cards (bets/assumptions/goals/
  // summary/decision), and the whole point of `WidgetUpdateSurface`'s in-place
  // skeleton refresh is that they visibly "refresh" where they already sit. The
  // "move" branch below re-mounts the card at a new tree position under a FRESH
  // React element, which — despite carrying the same `widget_id` — cannot reuse
  // the old `WidgetUpdateSurface` instance, so it played the pop+glow MOUNT cue
  // instead of the intended skeleton (the bug this fixes). The bounded window is
  // retained for any other (non-materialized) widget type that might reuse an id.
  const { flatIndex, total } = flatPosition(cleaned, loc);
  const isMaterialized = targetId.startsWith(MATERIALIZED_CARD_ID_PREFIX);
  if (isMaterialized || flatIndex >= total - dampingWindow) {
    return replaceBlockAt(cleaned, loc, block);
  }

  // (4) Move: append the new card at the frontier, then drop a breadcrumb where
  //     the old one sat. Appending first tells us which turn to point the chip at;
  //     the old card's index is unaffected because a frontier append only extends
  //     the END of the trailing turn (and the card, being outside the damping
  //     window, is never that trailing block).
  const appended = appendAtFrontier(cleaned, block, newTurnId, timestamp);
  const marker = makeCardMovedMarker(targetId, appended.turnId);
  return replaceBlockAt(appended.turns, loc, marker);
}

/**
 * Collapse duplicate live cards by `widget_id`, keeping only the LAST (frontier-most)
 * occurrence — the hydrate-merge both-singletons fix (T016, red-team F3.2). The
 * `/turns` rehydrate returns the final materialized card AND a live in-session copy
 * may already sit in `turns` after a deferred resume; without this both render.
 * Breadcrumb markers are left intact (their ids are distinct). Turns emptied by the
 * collapse are dropped. Non-colliding ids (all model-minted widgets) are untouched.
 */
export function dedupeCardsByWidgetId(turns: V2ConversationTurn[]): V2ConversationTurn[] {
  const lastLoc = new Map<string, WidgetLocation>();
  turns.forEach((turn, t) => {
    turn.content.forEach((block, b) => {
      if (block.type === 'widget' && !isCardMovedBlock(block)) {
        lastLoc.set(block.widget_id, { turnIndex: t, blockIndex: b });
      }
    });
  });

  const out: V2ConversationTurn[] = [];
  turns.forEach((turn, t) => {
    let changed = false;
    const content = turn.content.filter((block, b) => {
      if (block.type !== 'widget' || isCardMovedBlock(block)) return true;
      const loc = lastLoc.get(block.widget_id);
      const keep = loc !== undefined && loc.turnIndex === t && loc.blockIndex === b;
      if (!keep) changed = true;
      return keep;
    });
    if (content.length === 0 && changed) return; // duplicate-only turn disappears
    out.push(changed ? { ...turn, content } : turn);
  });
  return out;
}

/**
 * Widget types now OWNED by Cloud materialization (spec 004-p6). A block of one of
 * these types WITHOUT the deterministic `sc-card:` materialized id is a LEGACY
 * model-authored copy — Cloud materializes these now, so the model's copy is
 * dropped on rehydrate to avoid a double render (R10).
 */
export const CLOUD_OWNED_CARD_WIDGET_TYPES: ReadonlySet<string> = new Set([
  'sc_artifact_card',
  'sc_scope_summary_card',
]);

/** True for a legacy model-authored Cloud-owned card block (Cloud-owned type, non-materialized id). */
export function isLegacyModelAuthoredCard(block: ContentBlock): boolean {
  return (
    block.type === 'widget' &&
    !isCardMovedBlock(block) &&
    CLOUD_OWNED_CARD_WIDGET_TYPES.has(block.widget_type) &&
    !block.widget_id.startsWith(MATERIALIZED_CARD_ID_PREFIX)
  );
}

/** True for a materialized capture card block (deterministic engagement-scoped id). */
function isMaterializedCard(block: ContentBlock): block is WidgetBlock {
  return (
    block.type === 'widget' &&
    !isCardMovedBlock(block) &&
    block.widget_id.startsWith(MATERIALIZED_CARD_ID_PREFIX)
  );
}

/**
 * `/turns` rehydration final-state assembly (spec 004-p6, T018).
 *
 * Given the FULL hydrated transcript, produce the final on-screen state so a
 * scoping resume reads as "one card per kind that evolved", not a stack of every
 * revision:
 *   1. Legacy model-authored Cloud-owned card blocks are DROPPED (double-render fix
 *      — Cloud materializes these now; {@link isLegacyModelAuthoredCard}).
 *   2. Each materialized card (deterministic `sc-card:` id) renders ONCE at its
 *      FINAL (frontier-most) position; every earlier occurrence is replaced by a
 *      `card_moved` breadcrumb marker pointing at the final turn.
 * A turn emptied entirely by (1)/(2) disappears. Non-card content is untouched.
 * Pure + total; idempotent (a second pass finds each card already at its final
 * position and the markers already in place).
 */
export function assembleFinalStateTurns(turns: V2ConversationTurn[]): V2ConversationTurn[] {
  if (turns.length === 0) return turns;

  // Pass 1 — drop legacy model-authored Cloud-owned cards.
  const deLegacied = turns.map((turn) => {
    const content = turn.content.filter((b) => !isLegacyModelAuthoredCard(b));
    return content.length === turn.content.length ? turn : { ...turn, content };
  });

  // Locate the FINAL position of each materialized card id.
  const lastLoc = new Map<string, WidgetLocation>();
  deLegacied.forEach((turn, t) => {
    turn.content.forEach((block, b) => {
      if (isMaterializedCard(block)) lastLoc.set(block.widget_id, { turnIndex: t, blockIndex: b });
    });
  });
  if (lastLoc.size === 0) return deLegacied;

  // Pass 2 — keep each materialized card only at its final position; replace prior
  // occurrences with a `card_moved` marker pointing at the final turn.
  const out: V2ConversationTurn[] = [];
  deLegacied.forEach((turn, t) => {
    let changed = false;
    const content: ContentBlock[] = [];
    turn.content.forEach((block, b) => {
      if (isMaterializedCard(block)) {
        const loc = lastLoc.get(block.widget_id)!;
        const isFinal = loc.turnIndex === t && loc.blockIndex === b;
        if (isFinal) {
          content.push(block);
        } else {
          content.push(makeCardMovedMarker(block.widget_id, deLegacied[loc.turnIndex].turn_id));
          changed = true;
        }
        return;
      }
      content.push(block);
    });
    if (content.length === 0 && changed) return; // fully-collapsed turn disappears
    out.push(changed ? { ...turn, content } : turn);
  });
  return out;
}
